import React, { useState } from 'react';
import { ENV } from '../../config/env';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

interface PlanOption {
  id: string;
  name: string;
  price: number;
  interval: string;
  displayPrice: string;
}

const planOptions: PlanOption[] = [
  {
    id: 'monthly',
    name: 'Monthly Subscription',
    price: 19.00,
    interval: 'month',
    displayPrice: '£19.00/month'
  },
  {
    id: 'annual',
    name: 'Annual Subscription',
    price: 182.40,
    interval: 'year',
    displayPrice: '£182.40/year (Save 20%)'
  }
];

const GuestCheckout: React.FC = () => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Validate email format
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(email);
    
    if (!isValid) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
    
    return isValid;
  };

  const handleCheckout = async () => {
    // Validate email before proceeding
    if (!validateEmail(email)) {
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${ENV.API_URL}/api/payment/guest-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          planId: selectedPlan
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || 'Checkout failed');
      }
      
      const data = await response.json();
      if (data.url) {
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to process checkout');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    // Clear error when user starts typing again
    if (emailError) {
      setEmailError('');
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-lg">
      <h1 className="text-2xl font-bold mb-6 text-center">Subscribe to Explanatum</h1>
      
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="mb-4">
          <label className="block mb-2 font-medium">Email Address *</label>
          <input 
            type="email" 
            value={email} 
            onChange={handleEmailChange}
            className={`w-full p-3 border rounded ${emailError ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="your@email.com"
            required
          />
          {emailError && <p className="text-red-500 text-sm mt-1">{emailError}</p>}
          <p className="text-gray-500 text-sm mt-1">We'll send your receipt and account details to this address</p>
        </div>

        <div className="mb-6">
          <label className="block mb-2 font-medium">Select Plan</label>
          <div className="space-y-2">
            {planOptions.map(plan => (
              <div 
                key={plan.id}
                className={`border rounded p-4 cursor-pointer ${selectedPlan === plan.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                onClick={() => setSelectedPlan(plan.id)}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-medium">{plan.name}</h3>
                    <p className="text-gray-600">{plan.displayPrice}</p>
                  </div>
                  <div>
                    <div className={`w-5 h-5 rounded-full ${selectedPlan === plan.id ? 'bg-blue-500' : 'border border-gray-300'}`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button 
          onClick={handleCheckout}
          disabled={loading || !email}
          className={`w-full p-3 rounded text-white font-medium ${loading || !email ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
        >
          {loading ? 'Processing...' : 'Proceed to Payment'}
        </button>
      </div>

      <div className="text-center text-sm text-gray-500">
        <p>By subscribing, you agree to our Terms of Service and Privacy Policy</p>
      </div>
    </div>
  );
};

export default GuestCheckout;
